import { getImitationUserId } from "../lib/authToken";
import { normalizeApiUrl } from "../lib/apiUrl";
import { buildAssessmentPageLocation } from "../lib/eventer/assessmentEventLocation";
import { resolveAssessmentSessionId } from "../lib/eventer/assessmentEventSession";

type AssessmentAnalyticsPayload = {
  userID: string;
  location: string;
  action: string;
  timestamp: string;
  data?: Record<string, unknown>;
};

type PostAssessmentAnalyticsEventParams = {
  appApiUrl: string | null | undefined;
  token: string | null;
  userId: string | null | undefined;
  action: string;
  location?: string;
  timestamp?: string;
  data?: Record<string, unknown>;
  fetchImpl?: typeof fetch;
};

const ANALYTICS_DEDUPE_WINDOW_MS = 2000;
const recentAnalyticsEvents = new Map<string, number>();

function resolveImitationUserId(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  return getImitationUserId();
}

export function buildAssessmentAnalyticsUrl(
  appApiUrl: string | null | undefined,
): string | null {
  const normalizedUrl = normalizeApiUrl(appApiUrl);
  if (!normalizedUrl) {
    return null;
  }

  return `${normalizedUrl}/analytics`;
}

function shouldSkipDuplicateAnalyticsEvent(params: {
  userId: string;
  action: string;
  location: string;
  data?: Record<string, unknown>;
}): boolean {
  const dedupeKey = JSON.stringify({
    userId: params.userId,
    action: params.action,
    location: params.location,
    data: params.data ?? null,
  });
  const now = Date.now();
  const lastSentAt = recentAnalyticsEvents.get(dedupeKey);
  if (
    typeof lastSentAt === "number" &&
    now - lastSentAt < ANALYTICS_DEDUPE_WINDOW_MS
  ) {
    return true;
  }

  recentAnalyticsEvents.set(dedupeKey, now);
  return false;
}

export async function postAssessmentAnalyticsEvent({
  appApiUrl,
  token,
  userId,
  action,
  timestamp = new Date().toISOString(),
  location,
  data,
  fetchImpl = fetch,
}: PostAssessmentAnalyticsEventParams): Promise<void> {
  const analyticsUrl = buildAssessmentAnalyticsUrl(appApiUrl);
  const normalizedUserId = userId?.trim();
  const normalizedAction = action.trim();
  const resolvedLocation =
    typeof location === "string"
      ? location
      : buildAssessmentPageLocation(data?.stepKey);
  const normalizedPageLocation = resolvedLocation.trim();
  const assessmentSessionId = resolveAssessmentSessionId(
    normalizedUserId,
    normalizedPageLocation,
  );

  if (!analyticsUrl) {
    throw new Error("Assessment analytics API URL is not configured.");
  }
  if (!token) {
    throw new Error("Missing auth token.");
  }
  if (!normalizedUserId) {
    throw new Error("Missing user id.");
  }
  if (!normalizedAction) {
    throw new Error("Missing analytics action.");
  }
  if (!normalizedPageLocation) {
    throw new Error("Missing page location.");
  }

  if (
    shouldSkipDuplicateAnalyticsEvent({
      userId: normalizedUserId,
      action: normalizedAction,
      location: normalizedPageLocation,
      data,
    })
  ) {
    return;
  }

  const payload: AssessmentAnalyticsPayload = {
    userID: normalizedUserId,
    location: normalizedPageLocation,
    action: normalizedAction,
    timestamp,
    ...(assessmentSessionId ? { assessmentSessionId } : {}),
    ...(data || assessmentSessionId
      ? {
          data: {
            ...(data ?? {}),
          },
        }
      : {}),
  };

  const imitationUserId = resolveImitationUserId()?.trim();

  const response = await fetchImpl(analyticsUrl, {
    method: "POST",
    keepalive: true,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(imitationUserId ? { ImpersonatedUserId: imitationUserId } : {}),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Assessment analytics event failed (${response.status}).`);
  }
}
